/**
 * officeIdentity - who has an office, where its door is, and when you are a
 * GUEST in somebody else's books.
 *
 * Karr has two altitudes and they are not the same place:
 *
 *   the OFFICE   the consultant's own firm. Their clients, their roster, their
 *                work. One org, owned by them, flagged `is_practice`.
 *   a CLIENT'S   a business's books, which a consultant is invited into to keep
 *   BOOKS        the accounts and file the returns. They are a professional
 *                guest there, not a second owner.
 *
 * Four bugs came from conflating the two, and all four are answered here rather
 * than re-derived per surface:
 *
 *  1. **The Office pill showed to people with no office.** It keyed on "is this
 *     org a practice, or is any org I can see a practice", so a plan that
 *     happened to grant `practice_dashboard` (comps, legacy all-on plans), or a
 *     stray `is_practice` on a client's row, handed a firm-altitude control to a
 *     business owner who has no firm. Ownership is the test: {@link myOfficeOrg}.
 *  2. **The Office pill did not go to the office.** It navigated to the office
 *     ROUTE while leaving the active org alone, so a consultant standing in a
 *     client's books landed on that client's `/portfolio`, which answers
 *     "Practice Suite is turned off for your organization". The door has to
 *     change the org too; see `AppLayout`'s `goToOffice`.
 *  3. **A guest could read the host's private channels.** The Karr Phone is
 *     scoped to the active org, so entering a client's books handed the visiting
 *     accountant that client's WhatsApp inbox, their sent email and their
 *     referral earnings. See {@link CLIENT_PRIVATE_PHONE_APPS}, and the SQL half
 *     in `is_visiting_professional()` (migration, office-guest).
 *  4. **The header called a business's own admin a "Client".** The badge keyed
 *     on `role !== 'owner'`, which is true of every admin, accountant and viewer
 *     a business employs. {@link isVisitingClientBooks} is the honest test, and
 *     everyone else now wears their actual role.
 *
 * Pure (no React, no store) so every branch is unit-testable. `useOfficeIdentity`
 * is the hook that feeds it the session; surfaces should use that rather than
 * calling these with hand-assembled arguments.
 *
 * @since office-boundary (2026-08-10)
 */

/** Just enough of an `Org` to decide. Matches `types/db.ts`'s `Org`. */
export interface OfficeMembership {
    id: string;
    name?: string | null;
    role?: string | null;
    /** `orgs.is_practice` (ob-5): this org is a consultant's own firm. */
    is_practice?: boolean | null;
}

/**
 * The one door to the firm altitude.
 *
 * It used to be three: the header pill went to `/portfolio`, the "Back to
 * Office" button on the client banner went to `/practice` (a different, older
 * screen), and the post-login fork goes to `/practice-crm`. Two of those are
 * "the office" by the product's own word for it, so they now share this
 * constant and cannot drift apart again.
 *
 * The post-login fork keeps its own route deliberately and is NOT a fourth
 * door: `/portfolio` and `/practice-crm` are two tabs of the same office
 * (`PracticeTabs`), and opening the roster at sign-in is decision O8 in
 * `docs/ONBOARDING.md`. See `postLoginLanding.ts`.
 */
export const OFFICE_ROUTE = '/portfolio';

/**
 * The firm-altitude routes: pages that are about the office, never about one
 * set of books. Inside a client's org every one of them answers "Practice Suite
 * belongs to your firm", so they light the header's Office pill and they are
 * never a page to keep when the business changes. `/my-work` is a practice
 * employee's home (postLoginLanding O8), so it is the office too.
 */
export const OFFICE_ROUTES = ['/portfolio', '/practice-crm', '/requests', '/partner', '/my-work'] as const;

/** Is this path one of the office's own pages (or under one)? */
export function isOfficeRoute(path: string): boolean {
    return OFFICE_ROUTES.some(r => path === r || path.startsWith(r + '/'));
}

/** Where a business opens when you arrive at it from the office. */
export const CLIENT_LANDING_ROUTE = '/dashboard';

/**
 * Where to land after picking another business in the switcher, or null to
 * stay on the page you are on.
 *
 * Founder, 3 Oct 2026, switching from the office's CRM to Nilima and getting
 * the office's "Practice Suite belongs to your firm" wall inside Nilima's books:
 *
 *   office → client   the client's dashboard. The office page you were on has
 *                     no meaning in their books.
 *   client → client   the same page, now for the new client: you were checking
 *                     something across your clients, so keep checking it.
 *
 * An office page always gives way to the dashboard, whichever org you stood in
 * when you opened it, because it can only ever show that wall in a client's
 * books. Switching TO the office keeps the page; the Office pill is the door to
 * the office's own pages.
 */
export function landingAfterSwitch(args: {
    fromOrgId: string | null | undefined;
    toOrgId: string;
    officeOrgId: string | null | undefined;
    path: string;
}): string | null {
    const { fromOrgId, toOrgId, officeOrgId, path } = args;
    if (toOrgId === fromOrgId) return null;
    if (officeOrgId && toOrgId === officeOrgId) return null;
    const fromOffice = !!officeOrgId && fromOrgId === officeOrgId;
    return fromOffice || isOfficeRoute(path) ? CLIENT_LANDING_ROUTE : null;
}

/**
 * The office this person OWNS, or null.
 *
 * Ownership, not membership, and deliberately so: a practice employee is
 * `staff` and has their own home (`/my-work`), and someone merely invited into
 * a firm's org has a role there rather than a roster of their own. This is the
 * gate for every firm-altitude control.
 */
export function myOfficeOrg<T extends OfficeMembership>(orgs: readonly T[]): T | null {
    return orgs.find(o => o.role === 'owner' && o.is_practice === true) ?? null;
}

/** Do they run a practice? (ob-5, O8.) */
export function ownsPractice(orgs: readonly OfficeMembership[]): boolean {
    return myOfficeOrg(orgs) !== null;
}

/**
 * Any practice they belong to, owned first, else one they work at.
 *
 * Wider than {@link myOfficeOrg} on purpose: the PRIVACY rule has to cover the
 * firm's employees too. A staffer sent into a client's books is exactly as much
 * of an outsider as the partner who sent them.
 */
export function myPracticeMembership<T extends OfficeMembership>(orgs: readonly T[]): T | null {
    return myOfficeOrg(orgs) ?? orgs.find(o => o.is_practice === true) ?? null;
}

/**
 * Am I a visiting professional in these books, in someone else's org, on behalf
 * of my firm?
 *
 * True only when all three hold, so it can never fire on an ordinary business:
 *
 *   I belong to a practice at all (otherwise I am nobody's consultant), AND
 *   the org I'm standing in is not that practice, AND
 *   I am not its owner (a second business of my own is my own books).
 *
 * An org id that is not among the memberships answers TRUE: the role is
 * unknown, and the only unsafe answer to "may I read this org's private mail?"
 * is a guess of "yes".
 *
 * The SQL counterpart is `is_visiting_professional(uuid)`, and the two must stay
 * in step. This one hides the doors; that one is the lock.
 */
export function isVisitingClientBooks(
    currentOrgId: string | null | undefined,
    orgs: readonly OfficeMembership[],
): boolean {
    if (!currentOrgId) return false;
    const practice = myPracticeMembership(orgs);
    if (!practice || practice.id === currentOrgId) return false;
    const here = orgs.find(o => o.id === currentOrgId);
    return here ? here.role !== 'owner' : true;
}

/**
 * The Karr Phone apps that belong to the ORG, not to the person holding the
 * phone. Hidden while {@link isVisitingClientBooks}.
 *
 *   whatsapp   the business's own WhatsApp inbox: their conversations with
 *              their customers, vendors and staff, from their own number.
 *   mail       every email the business sent through Karr (the ow-1 outbox),
 *              and, with a connected mailbox, the ability to send AS them.
 *   referrals  their partner-programme signups and what those earned.
 *
 * What stays, and why it is not an oversight: **Messages** is the accountant to
 * client rail itself (and is keyed on the signed-in person, not the org),
 * **Requests** is the documents-I-asked-you-for rail, **Alerts** carries the
 * compliance warnings that are the whole reason the consultant is here, and
 * **Screen share** shares the consultant's own screen on request.
 */
export const CLIENT_PRIVATE_PHONE_APPS = ['whatsapp', 'mail', 'referrals'] as const;

export type ClientPrivatePhoneApp = (typeof CLIENT_PRIVATE_PHONE_APPS)[number];

/** Is this phone app the org's own private channel? */
export function isClientPrivatePhoneApp(app: string): app is ClientPrivatePhoneApp {
    return (CLIENT_PRIVATE_PHONE_APPS as readonly string[]).includes(app);
}

/**
 * Settings tabs that configure the ORG'S OWN identity to the outside world.
 * Hidden from a visiting professional for the same reason the phone apps are:
 * connecting, disconnecting or re-pointing a client's mailbox or WhatsApp
 * number is the client's decision about how their business speaks, and the
 * panels offer a Disconnect button to anyone who can reach the tab.
 */
export const CLIENT_PRIVATE_SETTINGS_TABS = ['email', 'whatsapp'] as const;

/** Is this Settings tab the org's own outward identity? */
export function isClientPrivateSettingsTab(tab: string): boolean {
    return (CLIENT_PRIVATE_SETTINGS_TABS as readonly string[]).includes(tab);
}

/**
 * The firm whose OWN AI key runs while I work in these books, or null (prokey).
 *
 * Founder, 16 Sep 2026: an auditor doing the tax audit, or a consultant keeping
 * a client's books, works on their own key, not the client's. "Their key" is the
 * one saved on their firm's books, under Settings > AI.
 *
 * The visiting rule is {@link isVisitingClientBooks}'s (a practice I belong to,
 * books that are not it, and not books I own), narrowed to ONE firm: the firm I
 * own, else the one firm I work at. Two firms and nothing to tell them apart is
 * null rather than a guess, because the answer decides whose provider bill the
 * work lands on.
 *
 * The server half is `firmForCallerAiKey` in
 * supabase/functions/_shared/workAiKey.ts, which is the one that actually picks
 * the key; tests/audit/workAiKey.test.ts runs both over the same cases.
 */
export function firmForMyAiKey<T extends OfficeMembership>(
    currentOrgId: string | null | undefined,
    orgs: readonly T[],
): T | null {
    if (!currentOrgId) return null;
    const firms = orgs.filter(o => o.is_practice === true && o.id !== currentOrgId);
    if (firms.length === 0) return null;
    const here = orgs.find(o => o.id === currentOrgId);
    if (here && here.role === 'owner') return null;
    const owned = firms.filter(o => o.role === 'owner');
    if (owned.length > 0) return owned.length === 1 ? owned[0] : null;
    return firms.length === 1 ? firms[0] : null;
}

/**
 * Which marks a person keeps in their signature vault: everyone has a
 * signature, somebody who practises also has a firm seal.
 *
 * The question is **does this person practise**, never *which org are they
 * standing in right now*. Settings used to ask `currentOrg.role === 'auditor'`,
 * which hid the seal from exactly the people who own one: a Chartered
 * Accountant in their own firm is `owner` there, not `auditor`, so the slot
 * appeared nowhere they would think to look and the seal read as missing. The
 * vault is keyed on the PERSON (`<user id>/seal.png`) and a seal belongs to a
 * firm rather than to one engagement, so a per-org answer was always the wrong
 * shape: the same CA has one seal, whichever client's books they are inside.
 *
 * Two ways to practise, and either alone is enough:
 *  - they own their own firm ({@link myOfficeOrg}, the office altitude), or
 *  - they hold the auditor's seat in somebody's books, anywhere.
 *
 * @since seal-1
 */
export function signatureKindsFor(orgs: readonly OfficeMembership[]): ('signature' | 'seal')[] {
    const practises = myOfficeOrg(orgs) !== null || orgs.some((o) => o.role === 'auditor');
    return practises ? ['signature', 'seal'] : ['signature'];
}
